import React, { useEffect, useRef, useState } from "react";
import API from "../config";
import { loadCompanyBranding, storeBranding } from "../utils/companyBranding";

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const Profile = () => {
  const token = localStorage.getItem("token");
  const [profile, setProfile] = useState(readUser());
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [savingLogo, setSavingLogo] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const logoInputRef = useRef(null);
  const canBrandCompany = ["company_admin", "super_admin", "system_manager"].includes(String(profile.role || "").toLowerCase());

  useEffect(() => {
    if (!token) return;

    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API}/users/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const nextProfile = res.ok ? data : readUser();
        setProfile(nextProfile);
        setForm((prev) => ({
          ...prev,
          name: nextProfile.name || "",
          email: nextProfile.email || "",
        }));
      } catch {
        const fallback = readUser();
        setProfile(fallback);
        setForm((prev) => ({ ...prev, name: fallback.name || "", email: fallback.email || "" }));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  useEffect(() => {
    loadCompanyBranding().then(setCompany).catch(() => setCompany(null));
  }, []);

  const updateCompanyLogo = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpeg|webp|svg\+xml)$/.test(file.type) || file.size > 2 * 1024 * 1024) {
      setError("Choose a PNG, JPEG, WebP, or SVG logo under 2 MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        setSavingLogo(true);
        setError("");
        const response = await fetch(`${API}/companies/${company.id}/logo`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ logo_data: reader.result }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || "Unable to update company logo");
        const nextCompany = { ...company, ...data };
        setCompany(nextCompany);
        storeBranding(nextCompany);
        setMessage("Company logo updated successfully");
      } catch (uploadError) {
        setError(uploadError.message || "Unable to update company logo");
      } finally {
        setSavingLogo(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const updateProfile = async () => {
    setMessage("");
    setError("");

    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }

    if (form.password && form.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      setSaving(true);
      const payload = { name: form.name.trim() };
      if (form.password) payload.password = form.password;

      const res = await fetch(`${API}/users/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.message || "Profile update failed");
        return;
      }

      const stored = readUser();
      const updatedUser = { ...stored, ...data, role: stored.role || data.role };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setProfile(updatedUser);
      setForm((prev) => ({ ...prev, password: "", confirmPassword: "" }));
      setMessage("Profile updated successfully");
    } catch {
      setError("Server error. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-slate-500">Loading profile...</div>;

  const initials = String(profile.name || profile.email || "U").slice(0, 2).toUpperCase();

  return (
    <div className="p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Profile</h1>
          <p className="mt-1 text-sm text-slate-500">Update your account name and password.</p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="flex flex-col items-center text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-green-600 text-2xl font-bold text-white">
                {initials}
              </div>
              <h2 className="mt-4 text-lg font-bold text-slate-900">{profile.name || "User"}</h2>
              <p className="text-sm text-slate-500">{profile.email}</p>
              <span className="mt-3 rounded-full bg-green-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-green-700">
                {profile.role || "user"}
              </span>
            </div>

            <div className="mt-6 space-y-3 border-t border-slate-100 pt-5 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-slate-400">Company</span>
                <span className="text-right font-medium text-slate-700">{profile.company_name || "App access"}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-slate-400">Account ID</span>
                <span className="font-mono text-slate-700">{profile.id || "-"}</span>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h2 className="text-lg font-bold text-slate-900">Account Details</h2>

              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-slate-500">Name</span>
                <input
                  value={form.name}
                  onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-200"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-slate-500">Email</span>
                <input value={form.email} disabled className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500" />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-slate-500">New Password</span>
                <input
                  type="password"
                  value={form.password}
                  onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
                  placeholder="Leave blank to keep current"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-200"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-slate-500">Confirm Password</span>
                <input
                  type="password"
                  value={form.confirmPassword}
                  onChange={(event) => setForm((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-200"
                />
              </label>
              </div>

              {error && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}
              {message && <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{message}</div>}

              <div className="mt-6 flex justify-end">
              <button
                onClick={updateProfile}
                disabled={saving}
                className="rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save Profile"}
              </button>
              </div>
            </div>

            {canBrandCompany && company && (
              <div className="rounded-xl border border-slate-200 bg-white p-6">
                <h2 className="text-lg font-bold text-slate-900">Company Branding</h2>
                <p className="mt-1 text-sm text-slate-500">This logo is shown in the sidebar and account badge for {company.company_name} users.</p>
                <div className="mt-5 flex flex-wrap items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 p-2">
                    {company.logo_data ? <img src={company.logo_data} alt={`${company.company_name} logo`} className="h-full w-full object-contain" /> : <span className="text-sm font-bold text-slate-400">Logo</span>}
                  </div>
                  <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={updateCompanyLogo} className="hidden" />
                  <button type="button" onClick={() => logoInputRef.current?.click()} disabled={savingLogo} className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60">
                    {savingLogo ? "Uploading..." : company.logo_data ? "Change Logo" : "Upload Logo"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
